import React from 'react';
import { getFormationCoords } from '../utils/gameData';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const ratingColor = (r) => {
  if (!r && r !== 0) return '#A0A0A5';
  if (r >= 8.5) return '#FFD700';
  if (r >= 7.5) return '#32D74B';
  if (r >= 6.5) return '#4DA3FF';
  if (r >= 5.5) return '#A0A0A5';
  return '#FF3B30';
};

const truncateName = (name, max = 12) => {
  if (!name) return '';
  // Prefer "F. Lastname" style for compact display
  const parts = name.split(/\s+/);
  if (parts.length >= 2) {
    const last = parts[parts.length - 1];
    const compact = `${parts[0][0]}. ${last}`;
    if (compact.length <= max) return compact;
    return last.length <= max ? last : last.slice(0, max - 1) + '…';
  }
  return name.length <= max ? name : name.slice(0, max - 1) + '…';
};

export const TacticalPitch = ({
  team,
  isAway = false,
  onPlayerClick,
  selectedPlayer,
  showTooltips = true,
  matchRatings = null,
}) => {
  if (!team) return null;

  const formationCoords = getFormationCoords(team.formation);

  const getPlayerByIndex = (index) => {
    const playerNumber = team.lineup[index];
    return team.players.find(p => p.number === playerNumber);
  };

  const getPositionColor = (position) => {
    if (position === 'GK') return 'position-gk';
    if (['CB', 'LB', 'RB', 'LWB', 'RWB', 'SW'].includes(position)) return 'position-def';
    if (['CDM', 'CM', 'CAM', 'LM', 'RM'].includes(position)) return 'position-mid';
    return 'position-att';
  };

  return (
    <div
      className="tactical-pitch w-full aspect-[3/4] rounded-sm overflow-hidden relative"
      data-testid={`tactical-pitch-${isAway ? 'away' : 'home'}`}
    >
      {/* Pitch markings */}
      <div className="pitch-center-line" />
      <div className="pitch-center-circle" />
      <div className="pitch-penalty-area-top" />
      <div className="pitch-penalty-area-bottom" />
      <div className="pitch-goal-area-top" />
      <div className="pitch-goal-area-bottom" />

      {/* Player nodes */}
      {formationCoords.map((coord, idx) => {
        const player = getPlayerByIndex(coord.playerIndex);
        if (!player) return null;

        const isSelected = selectedPlayer?.id === player.id;
        const yPos = isAway ? 100 - coord.y : coord.y;
        const rating = matchRatings ? matchRatings[player.id] : undefined;
        const ratingShown = rating !== undefined ? rating : (player.overall / 10);
        const ratingClr = ratingColor(ratingShown);
        const ratingText = rating !== undefined ? rating.toFixed(1) : `OVR ${player.overall}`;

        const playerNode = (
          <button
            key={player.id}
            data-testid={`player-node-${player.number}`}
            onClick={() => onPlayerClick?.(player)}
            className={`
              tactical-player-stack
              ${isAway ? 'tactical-player-stack--away' : 'tactical-player-stack--home'}
              ${isSelected ? 'tactical-player-stack--selected' : ''}
            `}
            style={{ left: `${coord.x}%`, top: `${yPos}%` }}
          >
            <span className={`tactical-jersey ${getPositionColor(player.position)}`}>
              <span className="tactical-jersey-num">{player.number}</span>
              <span className="tactical-jersey-shine" />
            </span>
            <span className="tactical-name-plate">
              <span className="tactical-name-text">{truncateName(player.name)}</span>
              <span
                className="tactical-rating-chip"
                style={{ background: ratingClr + '22', color: ratingClr, borderColor: ratingClr + '88' }}
              >
                {ratingText}
              </span>
            </span>
          </button>
        );

        if (!showTooltips) return playerNode;

        return (
          <TooltipProvider key={player.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                {playerNode}
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-[#1A1A1F] border-[#2C2C35] p-3">
                <div className="text-sm">
                  <div className="flex items-center gap-2">
                    <span className="nationality-flag">{player.flag}</span>
                    <p className="font-bold text-white">{player.name}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`position-badge ${getPositionColor(player.position)}`}>
                      {player.position}
                    </span>
                    <span className="font-mono text-[#A0A0A5]">OVR {player.overall}</span>
                    <span className="font-mono text-[#007AFF]">POT {player.potential}</span>
                  </div>
                  <p className="text-xs text-[#6E6E73] mt-1">Age {player.age} | {player.nationality}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}

      {/* Team name overlay */}
      <div className={`absolute ${isAway ? 'bottom-2' : 'top-2'} left-2 right-2`}>
        <div className="bg-black/60 backdrop-blur-sm rounded-sm px-3 py-1.5">
          <p className="font-heading font-bold text-white text-sm truncate">{team.name}</p>
          <p className="text-xs text-[#A0A0A5]">{team.formation}</p>
        </div>
      </div>
    </div>
  );
};
